import {inject, Injectable, signal} from '@angular/core';
import {firstValueFrom, forkJoin, Observable, of, Subject} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {
  ChatLibraryApiItem, ChatLibraryApiResponse, ChatLibraryCreative, CreativeLoadResult, CreativesMetadataResponse,
  ChatLibraryState, ChatLibraryOpenTrigger,
  LibraryGroup, CreativeCardProps,
} from '../models/chat-library.models';
import {CommunicationService} from '../../../../http/communication.service';
import {PosthogService} from '../../../../services/posthog.service';
import {StateService} from '../../../../services/state.service';
import {PosthogEventCode, PosthogEventName} from '../../../../services/types';


export type ChatLibraryData = { id: string, chatLibrary: ChatLibraryApiResponse | undefined, creativeUrls?: string[] };

const CHAT_LIBRARY_API = '/chat-library';
const CREATIVES_API = `${CHAT_LIBRARY_API}/creatives/metadata`;
export const CREATIVES_PAGE_SIZE = 12;
const INSIGHTS_URL_PREFIX = 'https://insights.browsi.com';
const SOCIAL_CREATIVE_IDENTIFIERS = ['/meta-creatives/', '/linkedin-creatives/'];

const INITIAL_STATE: ChatLibraryState = {
  isOpen: false,
  isLoading: false,
  isLoadingMore: false,
  hasContent: false,
  loadError: false,
  activeGroupId: null,
  groups: [],
};

@Injectable({providedIn: 'root'})
export class ChatLibraryService {
  private readonly communicationService = inject(CommunicationService);
  private readonly posthogService = inject(PosthogService);
  private readonly stateService = inject(StateService);

  private readonly _state = signal<ChatLibraryState>(INITIAL_STATE);
  readonly state = this._state.asReadonly();

  private readonly _scrollToGroup$ = new Subject<string>();
  readonly scrollToGroup$ = this._scrollToGroup$.asObservable();

  private readonly _scrollToPassage$ = new Subject<{ passageId: string; start: number; end: number }>();
  readonly scrollToPassage$ = this._scrollToPassage$.asObservable();

  private loadEpoch = 0;

  resetLibrary(): void {
    this.loadEpoch++;
    this._state.set(INITIAL_STATE);
  }

  setActiveGroup(groupId: string | null): void {
    this._state.update(current => ({...current, activeGroupId: groupId}));
  }

  openPanel(trigger: ChatLibraryOpenTrigger): void {
    const wasOpen = this._state().isOpen;
    this._state.update(current => ({...current, isOpen: true}));
    if (!wasOpen) {
      this.posthogService.capture(PosthogEventName.CHAT_LIBRARY_PANEL_OPENED, {
        [PosthogEventCode.OPEN_TRIGGER]: trigger
      });
    }
  }

  closePanel(): void {
    this._state.update(current => ({...current, isOpen: false}));
  }

  scrollToGroup(groupId: string): void {
    this._scrollToGroup$.next(groupId);
  }

  focusOnGroup(group: LibraryGroup): void {
    const { answerPassageId, textLocation } = group;
    this.setActiveGroup(group.id);
    this.scrollToPassage(answerPassageId, textLocation.start, textLocation.end);
  }

  scrollToPassage(passageId: string, start: number, end: number): void {
    this._scrollToPassage$.next({passageId, start, end});
  }

  clearLoadError(): void {
    this._state.update(current => ({...current, loadError: false}));
  }

  async loadCreativeLibrary(messages: ChatLibraryData[], append = false): Promise<CreativeLoadResult> {
    const allItemIds = messages.flatMap(m => this.getItemIds(m));
    if (allItemIds.length === 0) {
      return {found: 0, failed: false};
    }
    const epoch = this.loadEpoch;
    const loadingKey = append ? 'isLoadingMore' : 'isLoading';
    this._state.update(current => ({...current, [loadingKey]: true}));
    try {
      const creativesMetadata = await firstValueFrom(this.fetchCreativesData(allItemIds));
      if (epoch !== this.loadEpoch) {
        return {found: 0, failed: false};
      }
      if (creativesMetadata.creatives.length > 0) {
        this.setGroupsFromPolarisResponse(messages, creativesMetadata, append);
      }
      return {found: creativesMetadata.creatives.length, failed: !!creativesMetadata.failed};
    } finally {
      if (epoch === this.loadEpoch) {
        this._state.update(current => ({...current, [loadingKey]: false}));
      }
    }
  }

  private getItemIds(message: ChatLibraryData): string[] {
    if (message.chatLibrary?.chatLibrary.length) {
      return message.chatLibrary.chatLibrary.map(item => item.itemId);
    }
    return (message.creativeUrls ?? []).map(url => this.stripInsightsUrlPrefix(url));
  }

  private stripInsightsUrlPrefix(url: string): string {
    return url.startsWith(INSIGHTS_URL_PREFIX) ? url.slice(INSIGHTS_URL_PREFIX.length) : url;
  }

  extractCreativeIdFromUrl(url: string): string {
    const path = this.stripInsightsUrlPrefix(url);
    if (SOCIAL_CREATIVE_IDENTIFIERS.some(identifier => path.startsWith(identifier))) {
      return path;
    }
    const fileName = url.split('/').pop() ?? url;
    return fileName.replace(/\.[^.]+$/, '');
  }

  // The server sometimes returns a creative id with a different extension (e.g. .jpg)
  // than the itemId it was requested with (e.g. .mp4), even though it's the same asset.
  private stripExtension(value: string): string {
    return value.replace(/\.[^.]+$/, '');
  }

  private matchesItemId(value: string | undefined, itemId: string): boolean {
    return !!value && this.stripExtension(this.extractCreativeIdFromUrl(value)) === this.stripExtension(itemId);
  }

  // A social creative referenced by its bare id (e.g. 1019748521149095_0) resolves to a card whose
  // asset path differs from the reference, so match on the creative id as well.
  private matchesCreativeId(creative: ChatLibraryCreative, reference: string): boolean {
    return !!creative.creativeId && creative.creativeId === this.stripExtension(reference);
  }

  setGroupsFromPolarisResponse(messages: ChatLibraryData[], creativesResponse: CreativesMetadataResponse, append = false): void {
    const libraryGroups: LibraryGroup[] = this.buildGroups(messages, creativesResponse.creatives);
    this._state.update(current => {
      const groups = append ? this.mergeGroups(current.groups, libraryGroups) : libraryGroups;
      return {...current, groups, hasContent: groups.length > 0};
    });
  }

  private mergeGroups(existing: LibraryGroup[], incoming: LibraryGroup[]): LibraryGroup[] {
    const merged = [...existing];
    for (const group of incoming) {
      const index = merged.findIndex(g => g.id === group.id);
      if (index >= 0) {
        merged[index] = {...merged[index], cards: [...merged[index].cards, ...group.cards]};
      } else {
        merged.push(group);
      }
    }
    return merged;
  }

  private fetchCreativesData(itemIds: string[]): Observable<CreativesMetadataResponse> {
    const uniqueIds = [...new Set(itemIds)].filter(Boolean);
    const pages: string[][] = [];
    for (let i = 0; i < uniqueIds.length; i += CREATIVES_PAGE_SIZE) {
      pages.push(uniqueIds.slice(i, i + CREATIVES_PAGE_SIZE));
    }
    if (pages.length === 0) {
      return of<CreativesMetadataResponse>({creatives: []});
    }
    return forkJoin(pages.map(pageIds => this.fetchCreativesPage(pageIds))).pipe(
      map(responses => ({creatives: responses.flatMap(r => r.creatives), failed: responses.some(r => r.failed)}))
    );
  }

  private fetchCreativesPage(pageIds: string[]): Observable<CreativesMetadataResponse> {
    const epoch = this.loadEpoch;
    return this.communicationService
      .httpPost<CreativesMetadataResponse>(CREATIVES_API, {ids: pageIds, accountKey: this.stateService.activeAccount()})
      .pipe(
        catchError(error => {
          console.error('[ChatLibraryService] fetchCreativesData failed', error);
          // A request from a previous chat must not flag the chat that is open now.
          if (epoch === this.loadEpoch) {
            this._state.update(current => ({...current, loadError: true}));
          }
          return of<CreativesMetadataResponse>({creatives: [], failed: true});
        })
      );
  }

  private buildGroups(
    messages: ChatLibraryData[],
    creatives: ChatLibraryCreative[]
  ): LibraryGroup[] {
    const groups: LibraryGroup[] = [];

    for (const message of messages) {
      if (!message.chatLibrary?.chatLibrary.length) {
        groups.push(...this.buildGroupFromCreativeUrls(message.creativeUrls ?? [], creatives, message.id));
        continue;
      }

      const grouped = new Map<string, ChatLibraryApiItem[]>();
      for (const item of message.chatLibrary.chatLibrary) {
        const existing = grouped.get(item.itemGroupId) ?? [];
        grouped.set(item.itemGroupId, [...existing, item]);
      }

      for (const [groupId, groupItems] of grouped.entries()) {
        const cards = groupItems
          .map(gi => {
            const creative = creatives.find(c =>
              this.matchesItemId(c.id, gi.itemId) || this.matchesItemId(c.thumbnailUrl, gi.itemId) || this.matchesCreativeId(c, gi.itemId));
            return creative ? {...creative, creativeUrl: creative.id} : undefined;
          })
          .filter((c): c is CreativeCardProps => !!c);

        const groupLabel = message.chatLibrary.groups.find(g => g.groupId === groupId);

        groups.push({
          id: groupId,
          label: groupLabel?.label || `Creatives Group`,
          answerPassageId: message.id,
          textLocation: groupLabel?.location || {start: 0, end: 0},
          dateRange: groupLabel?.dateRange,
          cards: cards.slice(0, 8),
        });
      }
    }
    return groups;
  }

  private buildGroupFromCreativeUrls(
    creativeUrls: string[],
    creatives: ChatLibraryCreative[],
    answerId: string
  ): LibraryGroup[] {
    const cards = creativeUrls
      .map(url => {
        const creativeId = this.extractCreativeIdFromUrl(url);
        const creativeMatch = creatives.find(c => c.id === creativeId || c.thumbnailUrl === creativeId || this.matchesCreativeId(c, creativeId));
        // The server's id is the real asset path; the message url may only carry the creative id.
        return creativeMatch ? {...creativeMatch, creativeUrl: creativeMatch.id} : undefined;
      })
      .filter((c): c is CreativeCardProps => !!c);

    if (!cards.length) {
      return [];
    }

    return [{
      id: answerId,
      label: 'Creatives',
      answerPassageId: answerId,
      textLocation: {start: 0, end: 0},
      cards: cards,
    }];
  }
}
